/* eslint-disable @wyz/module/no-relative-import */
import * as React from 'react';
import { useManageUsers } from '~/common/api';
import { useApplicationContext } from '~/common/context';
import { FEATURES, ROLES } from '~/common/helpers';
import { useAccessControl, useUserScopedState } from '~/common/hooks';
import {
  IEditableFunctionalContextState,
  IFunctionalContext,
} from '~/purchase/types';
import { FunctionalContext } from './contexts';
import { usePurchase } from './purchase-provider';

// Exported so modules that can't be wrapped in <FunctionalProvider> (e.g. the ODR
// operations page, outside the purchase route tree) share the same selected PDV via
// useUserScopedState. Namespace, not the storage key: it gets suffixed with the user id.
export const PURCHASE_STATE_STORAGE_KEY = '__WYZ_PURCHASE_STATE__';
export function FunctionalProvider(props: React.PropsWithChildren) {
  const purchaseContext = usePurchase();
  const { auth } = useApplicationContext();
  const acl = useAccessControl();
  const [contextState, setContext] =
    useUserScopedState<IEditableFunctionalContextState>(
      PURCHASE_STATE_STORAGE_KEY,
      {
        displayAdverts: true,
        selectedPrimaryCustomerId: undefined,
        selectedSecondaryCustomerId: undefined,
      },
    );

  const managePrimaryUser = useManageUsers({
    getUserCustomerById: {
      args: {
        userId: auth.userId as string,
        customerId: contextState.selectedPrimaryCustomerId || '',
      },
      enabled: !!contextState.selectedPrimaryCustomerId && !!auth.userId,
      refetchOnMount: true,
      staleTime: 0,
      gcTime: 0,
    },
  });
  const manageSecondaryUser = useManageUsers({
    getUserCustomerById: {
      args: {
        userId: auth.userId as string,
        customerId: contextState.selectedSecondaryCustomerId || '',
      },
      enabled: !!contextState.selectedSecondaryCustomerId && !!auth.userId,
      refetchOnMount: true,
      staleTime: 0,
      gcTime: 0,
    },
  });

  const context: IFunctionalContext = React.useMemo(() => {
    const primaryCustomer = managePrimaryUser.getUserCustomerById.data?.data;
    const secondaryCustomer =
      manageSecondaryUser.getUserCustomerById.data?.data;
    return {
      state: {
        ...contextState,
        primaryCustomer,
        secondaryCustomer,
        targetCustomer: purchaseContext.isCallCenterMode
          ? secondaryCustomer
          : primaryCustomer,
        targetCustomerId: purchaseContext.isCallCenterMode
          ? contextState.selectedSecondaryCustomerId
          : contextState.selectedPrimaryCustomerId,
        isPrimaryCustomerNotFound:
          managePrimaryUser.getUserCustomerById.isError,
        loading: {
          isLoadingPrimaryCustomer:
            managePrimaryUser.getUserCustomerById.isProcessing,
          isLoadingSecondaryCustomer:
            manageSecondaryUser.getUserCustomerById.isProcessing,
        },
      },
      handlers: {
        setPrimaryCustomerId: (customerId) => {
          setContext((prev) => ({
            ...prev,
            selectedPrimaryCustomerId: customerId,
          }));
        },
        setSecondaryCustomerId: (customerId) => {
          setContext((prev) => ({
            ...prev,
            selectedSecondaryCustomerId: customerId,
          }));
        },
        clearSelectedCustomers: () => {
          setContext((prev) => ({
            ...prev,
            selectedPrimaryCustomerId: undefined,
            selectedSecondaryCustomerId: undefined,
          }));
        },
        showAdverts: () => {
          setContext((prev) => ({ ...prev, displayAdverts: true }));
        },
        hideAdverts: () => {
          setContext((prev) => ({ ...prev, displayAdverts: false }));
        },
      },
      isCallCenterMode: purchaseContext.isCallCenterMode,
      mode: purchaseContext.mode,
    };
  }, [
    contextState,
    managePrimaryUser.getUserCustomerById.data?.data,
    managePrimaryUser.getUserCustomerById.isError,
    managePrimaryUser.getUserCustomerById.isProcessing,
    manageSecondaryUser.getUserCustomerById.data?.data,
    manageSecondaryUser.getUserCustomerById.isProcessing,
    purchaseContext.isCallCenterMode,
    purchaseContext.mode,
    setContext,
  ]);

  // Usetiful marker: present only when the user can create quotes AND the selected
  // PDV allows express quotes — same condition as the express tab in QuoteModeSwitcher.
  // Rendered here rather than in the Sidebar, which sits outside this provider and
  // therefore can't follow the selected PDV.
  const hasExpressQuoteAccess =
    acl.isEnabledFeatures([FEATURES.QUOTE, FEATURES.QUOTE_EXPRESS]) &&
    acl.isGranted([ROLES.ROLE_QUOTE_CREATE], 'affirmative') &&
    Boolean(context.state.targetCustomer?.expressQuoteSearchable);

  return (
    <FunctionalContext.Provider value={context}>
      {hasExpressQuoteAccess && (
        <span id='menu-express-quote-access' hidden aria-hidden='true' />
      )}
      {props.children}
    </FunctionalContext.Provider>
  );
}

export const usePurchaseContext = () => {
  const context = React.useContext(FunctionalContext);
  if (context === undefined) {
    throw new Error(
      'usePurchaseContext must be used within a FunctionalContext',
    );
  }
  return context;
};
